import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Award,
  Calendar,
  CheckCircle,
  ChevronRight,
  Clock,
  Eye,
  FileText,
  Grid3x3,
  List,
  PenLine,
  Play,
  Search,
  Star,
  Target,
  Trophy
} from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';

// Shared Components
import SimpleSelect from '@/components/ui/SimpleSelect';

// Services
import { certificationService, UserCertificationSummaryDto } from '@/services/certification.service';
import { reviewService } from '@/services/review.service';
import apiClient from '@/services/api';
import { API_ENDPOINTS } from '@/config/api.config';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { LEARNER_CERTIFICATIONS_PERMISSION } from '@/constants/constant-permissions';
import { CardSkeleton } from '@/components/ui/SkeletonLoader';

// Exam Attempt Types
interface ExamAttemptSummary {
  id: string;
  attempt_number: number;
  status: 'IN_PROGRESS' | 'SUBMITTED' | 'COMPLETED' | 'ABANDONED';
  started_at: string;
  submitted_at?: string;
  completed_at?: string;
  score: number;
  percentage: number;
  passed: boolean;
}

// Types - Extending API response with additional UI properties
interface CertificationWithProgress extends UserCertificationSummaryDto {
  // Additional computed properties for UI
  exam_attempts?: number;
  max_attempts?: number;
  best_score?: number;
  certificate_expiry_date?: Date;
  attempts?: ExamAttemptSummary[];
  inProgressAttemptId?: string;
  completedAttempts?: number;
  abandonedAttempts?: number;
  hasCompletedAttempt?: boolean; // True if any attempt has COMPLETED or SUBMITTED status
  hasPassedAttempt?: boolean; // True if any attempt has COMPLETED/SUBMITTED status AND passed is true
  show_correct_answers?: boolean; // Flag to show correct answers in exam results
  rating?: number;
  review_count?: number;
}

// Dashboard stats from API
interface DashboardStats {
  total_certification: number;
  completed_certification: number;
  in_progress_certification: number;
  mandatory_certification: number;
  total_time_spent_hours: number;
  recent_activity?: any[];
  upcoming_deadlines?: Array<{
    certification_id: string;
    certification_title: string;
    deadline_date: string;
  }>;
}

// Compact Certification Card Component
const CompactCertificationCard = ({
  certification,
  onAction,
  onRatingClick,
  onWriteReview,
  onClaimCertificate,
  hasUserReview,
  certificateNumber,
  canRead,
  canWrite
}: {
  certification: CertificationWithProgress;
  onAction: (certificationId: string, action: 'start' | 'continue' | 'preview' | 'viewResult') => void;
  onRatingClick: (certificationId: string) => void;
  onWriteReview: (certificationId: string) => void;
  onClaimCertificate?: (certificateNumber: string) => void;
  hasUserReview: boolean;
  certificateNumber?: string;
  canRead: boolean;
  canWrite: boolean;
}) => {
  const isOverdue = certification.deadline_date && new Date(certification.deadline_date) < new Date();
  const daysLeft = certification.deadline_date ?
    Math.ceil((new Date(certification.deadline_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null;

  const isExpired = certification.certificate_expiry_date &&
    new Date(certification.certificate_expiry_date) < new Date();

  return (
    <div className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition-all duration-200 overflow-hidden group flex flex-col h-full">
      {/* Thumbnail */}
      <div className="relative h-36 bg-gradient-to-br from-brand-amber-50 to-brand-orange-100 overflow-hidden flex-shrink-0">
        {certification.thumbnail_image_url || certification.banner_image_url ? (
          <img
            src={certification.thumbnail_image_url || certification.banner_image_url}
            alt={certification.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
            <Award className="w-12 h-12 text-brand-amber-400" />
          </div>
        )}

        {/* Status Badge */}
        <div className="absolute top-2 right-2 flex gap-1 flex-wrap">
          {certification.is_mandatory && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              Mandatory
            </span>
          )}
          {certification.certificate_issued && !isExpired && (
            <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Certified
            </span>
          )}
          {isExpired && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              Expired
            </span>
          )}
        </div>

        {/* Progress Bar */}
        {certification.status === 'IN_PROGRESS' && certification.total_time_spent_seconds && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/20 backdrop-blur-sm">
            <div className="h-1 bg-white/30">
              <div
                className="h-full bg-white transition-all duration-300"
                style={{
                  width: `${certification.time_limit_minutes
                    ? Math.min((certification.total_time_spent_seconds / (certification.time_limit_minutes * 60)) * 100, 100)
                    : 0}%`
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Content - Flex grow to push buttons to bottom */}
      <div className="p-3 flex flex-col flex-grow">
        {/* Title - Fixed 2 lines height */}
        <h3 className="font-medium text-sm text-gray-900 line-clamp-2 mb-2 group-hover:text-brand-amber-600 transition-colors min-h-[2.5rem]">
          {certification.title}
        </h3>

        {/* Description - 2 lines */}
        <p className="text-xs text-gray-600 line-clamp-2 mb-2 min-h-[2rem]">
          {certification.description || 'No description available'}
        </p>

        {/* Meta Info */}
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-2 flex-wrap">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {certification.time_limit_minutes || 0}m exam
          </span>
          {/* Rating Display - Clickable */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRatingClick(certification.certification_id);
            }}
            className="flex items-center gap-1 hover:bg-yellow-50 px-1.5 py-0.5 rounded-md transition-colors cursor-pointer"
            title="View reviews"
          >
            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
            <span className="font-medium text-gray-700">{certification.rating?.toFixed(1) || '0.0'}</span>
            <span className="text-gray-400">• {certification.review_count || 0} ratings</span>
          </button>
        </div>

        {/* Status Info */}
        {certification.status !== 'NOT_STARTED' && (
          <div className="text-xs text-gray-600 mb-2">
            {certification.status === 'COMPLETED' && certification.completed_at && certification.max_attempts !== null && (
              <span className="text-green-600 font-medium">
                Completed on {new Date(certification.completed_at).toLocaleDateString()}
              </span>
            )}
            {certification.status === 'IN_PROGRESS' && certification.started_at && (
              <span>Started on {new Date(certification.started_at).toLocaleDateString()}</span>
            )}
          </div>
        )}

        {/* Deadline and Attempts */}
        {certification.deadline_date && certification.status !== 'COMPLETED' && (
          <div className={`text-xs mb-2 ${isOverdue ? 'text-red-600' : daysLeft && daysLeft <= 7 ? 'text-orange-600' : 'text-gray-500'}`}>
            {isOverdue ? (
              <span className="flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Overdue
              </span>
            ) : (
              <div className="flex items-center justify-between">
                <span>Due in {daysLeft} days</span>
                {/* Show attempts only if allow_retry is not false */}
                {certification.max_attempts && certification.allow_retry !== false && (
                  <span className="text-gray-600">
                    {certification.completedAttempts || 0}/{certification.max_attempts} attempts
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Attempts Info (when no deadline) - Only show if max_attempts is defined and allow_retry is not false */}
        {!certification.deadline_date && certification.max_attempts && certification.allow_retry !== false && (
          <div className="text-xs text-gray-600 mb-2">
            Attempts: {certification.completedAttempts || 0}/{certification.max_attempts}
          </div>
        )}

        {/* Unlimited attempts message - when max_attempts is null */}
        {certification.max_attempts === null && (
          <div className="text-xs text-green-600 font-medium mb-2 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Unlimited attempts
          </div>
        )}

        {/* No Retry Allowed Message */}
        {certification.allow_retry === false && certification.max_attempts !== null && (
          <div className="text-xs text-orange-600 font-medium mb-2 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            No retry allowed
          </div>
        )}

        {/* Spacer to push buttons to bottom */}
        <div className="flex-grow"></div>

        {/* Action Buttons - Fixed to bottom */}
        <div className="flex flex-col gap-2 mt-auto">
          <div className="flex gap-2">
            <button
              onClick={() => canRead && onAction(certification.certification_id, 'preview')}
              disabled={!canRead}
              title={!canRead ? 'You do not have permission to access certifications' : undefined}
              className={`flex-1 px-2 py-2 sm:py-1.5 border rounded-lg transition-colors text-xs font-medium ${
                canRead
                  ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  : 'border-gray-200 text-gray-400 opacity-50 cursor-not-allowed'
              }`}
            >
              View
            </button>
            {(() => {
              const hasInProgressAttempt = !!certification.inProgressAttemptId;
              // max_attempts null means unlimited, so never reached
              const maxAttemptsReached = certification.max_attempts !== null && certification.max_attempts &&
                (certification.completedAttempts || 0) >= certification.max_attempts;
              const hasCompletedOrAbandoned = (certification.completedAttempts || 0) > 0 || (certification.abandonedAttempts || 0) > 0;

              // Show "Continue" if there's an in-progress attempt
              if (hasInProgressAttempt) {
                return (
                  <button
                    onClick={() => canWrite && onAction(certification.certification_id, 'continue')}
                    disabled={!canWrite}
                    title={!canWrite ? 'You do not have permission to access certifications' : undefined}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 sm:py-1.5 rounded-lg transition-colors text-xs font-medium ${
                      canWrite
                        ? 'bg-brand-blue-600 text-white hover:bg-brand-blue-700'
                        : 'bg-brand-blue-600 text-white opacity-50 cursor-not-allowed'
                    }`}
                  >
                    Continue
                    <ChevronRight className="w-3 h-3" />
                  </button>
                );
              }

              // Show "Completed" if max attempts reached (only when max_attempts is defined)
              if (maxAttemptsReached) {
                return (
                  <button
                    disabled
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-2 sm:py-1.5 bg-gray-400 text-white rounded-lg cursor-not-allowed text-xs font-medium"
                  >
                    <CheckCircle className="w-3 h-3" />
                    Completed
                  </button>
                );
              }

              // Show "Retry" if completed/abandoned but not reached max attempts and allow_retry is true
              if (hasCompletedOrAbandoned && !maxAttemptsReached) {
                const canRetry = certification.allow_retry !== false && canWrite;
                return (
                  <button
                    onClick={() => canRetry && onAction(certification.certification_id, 'start')}
                    disabled={!canRetry}
                    title={!canWrite ? 'You do not have permission to access certifications' : undefined}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 sm:py-1.5 rounded-lg transition-colors text-xs font-medium ${
                      canRetry
                        ? 'bg-orange-600 text-white hover:bg-orange-700'
                        : 'bg-gray-400 text-white cursor-not-allowed opacity-50'
                    }`}
                  >
                    Retry
                    <ChevronRight className="w-3 h-3" />
                  </button>
                );
              }

              // Default: Show "Start Exam"
              return (
                <button
                  onClick={() => canWrite && onAction(certification.certification_id, 'start')}
                  disabled={!canWrite}
                  title={!canWrite ? 'You do not have permission to access certifications' : undefined}
                  className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 sm:py-1.5 rounded-lg transition-colors text-xs font-medium ${
                    canWrite
                      ? 'bg-brand-amber-600 text-white hover:bg-brand-amber-700'
                      : 'bg-brand-amber-600 text-white opacity-50 cursor-not-allowed'
                  }`}
                >
                  Start Exam
                  <ChevronRight className="w-3 h-3" />
                </button>
              );
            })()}
          </div>
          {/* Claim Certificate Button - Only shown if certificate exists */}
          {certificateNumber && onClaimCertificate && (
            <button
              onClick={() => onClaimCertificate(certificateNumber)}
              className="w-full flex items-center justify-center gap-1 px-2 py-2 sm:py-1.5 bg-brand-amber-500 text-white hover:bg-brand-amber-600 rounded-lg transition-colors text-xs font-medium"
            >
              <Award className="w-3 h-3" />
              Claim Certificate
            </button>
          )}
          {/* View Result Button - Shown if show_feedback is true AND at least one attempt exists (completed, submitted, or abandoned) */}
          {(() => {
            const canViewResults = certification.show_feedback !== false; // Default to true if not specified
            const hasViewableAttempt =
              certification.hasCompletedAttempt === true ||
              (certification.abandonedAttempts ?? 0) > 0;
            if (!canViewResults || !hasViewableAttempt) return null;

            return (
              <button
                onClick={() => onAction(certification.certification_id, 'viewResult')}
                className="w-full flex items-center justify-center gap-1 px-2 py-2 sm:py-1.5 border border-brand-amber-600 text-brand-amber-600 hover:bg-brand-amber-50 cursor-pointer rounded-lg transition-colors text-xs font-medium"
              >
                <FileText className="w-3 h-3" />
                View Results
              </button>
            );
          })()}
          {/* Write Review Button - Hidden if user already reviewed */}
          {!hasUserReview && (
            <button
              onClick={() => canWrite && onWriteReview(certification.certification_id)}
              disabled={!canWrite}
              title={!canWrite ? 'You do not have permission to write reviews' : undefined}
              className={`w-full flex items-center justify-center gap-1 px-2 py-2 sm:py-1.5 border rounded-lg transition-colors text-xs font-medium ${
                canWrite
                  ? 'border-brand-purple-600 text-brand-purple-600 hover:bg-brand-purple-50 cursor-pointer'
                  : 'border-gray-200 text-gray-400 opacity-50 cursor-not-allowed'
              }`}
            >
              <PenLine className="w-3 h-3" />
              Write a Review
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function LearnerCertifications() {
  // Permission guard
  usePermissionRouteAccess([
    LEARNER_CERTIFICATIONS_PERMISSION.LEARNER_CERTIFICATIONS_READ,
    LEARNER_CERTIFICATIONS_PERMISSION.LEARNER_CERTIFICATIONS_WRITE,
  ]);
  const canRead = useHasPermission([LEARNER_CERTIFICATIONS_PERMISSION.LEARNER_CERTIFICATIONS_READ, LEARNER_CERTIFICATIONS_PERMISSION.LEARNER_CERTIFICATIONS_WRITE]);
  const canWrite = useHasPermission([LEARNER_CERTIFICATIONS_PERMISSION.LEARNER_CERTIFICATIONS_WRITE]);

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'in_progress' | 'completed' | 'not_started' | 'all'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('lastAccessed');

  // Fetch user certifications with progress and attempts
  const { data: certificationsData, isLoading: certificationsLoading } = useQuery({
    queryKey: ['learner-certifications'],
    queryFn: async () => {
      try {
        const data = await certificationService.getUserCertifications();
        console.log('📋 Certifications Data:', data);

        // Fetch attempts for each certification
        const certificationsWithAttempts = await Promise.all(
          data.map(async (cert) => {
            try {
              // Fetch attempts for this certification
              const attemptsResponse = await apiClient.get(
                API_ENDPOINTS.CERTIFICATIONS.LIST_ATTEMPTS(cert.certification_id)
              );

              if (attemptsResponse.data.success && attemptsResponse.data.data) {
                const attempts = attemptsResponse.data.data as ExamAttemptSummary[];

                // Find in-progress attempt
                const inProgressAttempt = attempts.find(a => a.status === 'IN_PROGRESS');

                // Count completed and abandoned attempts separately
                const completedAttempts = attempts.filter(
                  a => a.status === 'COMPLETED' || a.status === 'SUBMITTED'
                ).length;
                const abandonedAttempts = attempts.filter(a => a.status === 'ABANDONED').length;

                // Check if any attempt has COMPLETED or SUBMITTED status
                const hasCompletedAttempt = attempts.some(
                  a => a.status === 'COMPLETED' || a.status === 'SUBMITTED'
                );

                // Check if any attempt has passed
                const hasPassedAttempt = attempts.some(
                  a => (a.status === 'COMPLETED' || a.status === 'SUBMITTED') && a.passed === true
                );

                // Total attempts used = completed + abandoned (in-progress doesn't count yet)
                const totalAttemptsUsed = completedAttempts + abandonedAttempts;

                return {
                  ...cert,
                  attempts,
                  inProgressAttemptId: inProgressAttempt?.id,
                  completedAttempts: totalAttemptsUsed, // This shows total used attempts
                  abandonedAttempts,
                  hasCompletedAttempt, // Track if any attempt is completed/submitted
                  hasPassedAttempt, // Track if any attempt is passed
                  max_attempts: cert.max_attempts // Keep null as null - means no attempts allowed
                } as CertificationWithProgress;
              }

              // If no attempts data, return cert with defaults
              return {
                ...cert,
                attempts: [],
                completedAttempts: 0,
                abandonedAttempts: 0,
                hasCompletedAttempt: false,
                hasPassedAttempt: false,
                max_attempts: cert.max_attempts // Keep null as null - means no attempts allowed
              } as CertificationWithProgress;
            } catch (error) {
              console.error(`Failed to fetch attempts for certification ${cert.certification_id}:`, error);
              // Return cert with defaults on error
              return {
                ...cert,
                attempts: [],
                completedAttempts: 0,
                abandonedAttempts: 0,
                hasCompletedAttempt: false,
                hasPassedAttempt: false,
                max_attempts: cert.max_attempts // Keep null as null - means no attempts allowed
              } as CertificationWithProgress;
            }
          })
        );

        return certificationsWithAttempts;
      } catch (error) {
        console.error('Failed to fetch certifications:', error);
        toast.error('Failed to load your certifications');
        return [];
      }
    }
  });

  // Fetch dashboard stats
  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['learner-certification-dashboard-stats'],
    queryFn: async () => {
      try {
        const response = await apiClient.get(API_ENDPOINTS.CERTIFICATIONS.DASHBOARD);

        if (response.data.success) {
          return response.data.data as DashboardStats;
        }

        return null;
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
        return null;
      }
    }
  });

  const rawCertifications = certificationsData || [];
  const isLoading = certificationsLoading || statsLoading;

  // Fetch review stats for all certifications (single API call)
  const { data: reviewStatsMap } = useQuery({
    queryKey: ['learner-certification-review-stats-bulk'],
    queryFn: () => reviewService.getBatchCertificationStats(),
  });

  // Fetch user's existing reviews to check if they already reviewed
  const { data: myReviewsData } = useQuery({
    queryKey: ['learner-my-reviews'],
    queryFn: () => reviewService.getMyReviews(),
  });

  // Create a Set of certification IDs that the user has already reviewed
  const reviewedCertificationIds = new Set(
    myReviewsData?.reviews
      ?.filter(review => review.certification_id)
      ?.map(review => review.certification_id) || []
  );

  // Build certification_id -> certificate_number map from certificationsData (returned by my-certification API)
  const certCertificateMap = (() => {
    const map = new Map<string, string>();
    rawCertifications.forEach(cert => {
      if (cert.certificate_number) {
        map.set(cert.certification_id, cert.certificate_number);
      }
    });
    return map;
  })();

  // Handle claim certificate - navigate to public verification page
  const handleClaimCertificate = (certificateNumber: string) => {
    navigate(`/certificate/verify/${certificateNumber}`);
  };

  // Enrich certifications with review stats
  const certifications = rawCertifications.map((certification: CertificationWithProgress) => {
    const stats = reviewStatsMap?.get(certification.certification_id);
    return {
      ...certification,
      rating: stats?.avg_rating ?? certification.rating ?? 0,
      review_count: stats?.total_reviews ?? certification.review_count ?? 0,
    };
  });

  // Handle rating click - redirect to preview with reviews tab
  const handleRatingClick = (certificationId: string) => {
    navigate(`/learn/certification/${certificationId}?tab=reviews`);
  };

  // Handle write review click - redirect to preview with reviews tab and write action
  const handleWriteReview = (certificationId: string) => {
    navigate(`/learn/certification/${certificationId}?tab=reviews&action=write`);
  };

  // Handle certification action
  const handleCertificationAction = (certificationId: string, action: 'preview' | 'start' | 'continue' | 'viewResult') => {
    // Find the certification to get attempt info
    const certification = certifications.find(c => c.certification_id === certificationId);

    switch (action) {
      case 'start':
        // Starting new exam - no attempt ID
        navigate(`/learn/exams/start/${certificationId}`);
        break;
      case 'continue':
        // Continuing existing exam - pass attempt ID if available
        if (certification?.inProgressAttemptId) {
          navigate(`/learn/exams/start/${certificationId}`, {
            state: {
              inProgressAttemptId: certification.inProgressAttemptId,
              hasInProgressAttempt: true
            }
          });
        } else {
          // Fallback if no attempt ID found
          navigate(`/learn/exams/start/${certificationId}`);
        }
        break;
      case 'preview':
        navigate(`/learn/certification/${certificationId}`);
        break;
      case 'viewResult':
        navigate(`/learn/exams/results/${certificationId}`, {
          state: {
            show_correct_answers: certification?.show_correct_answers
          }
        });
        break;
      default:
        break;
    }
  };

  // Filter certifications based on active tab and search
  const filteredCertifications = certifications.filter(cert => {
    const matchesSearch = searchTerm === '' ||
      cert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.description?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesTab = false;

    if (activeTab === 'all') {
      matchesTab = true;
    } else if (activeTab === 'in_progress') {
      // Show in IN_PROGRESS tab if:
      // 1. User has attempts (started the certification) BUT
      // 2. None of the attempts have passed=true AND
      // 3. Attempts < max_attempts (still have attempts left)
      const hasAttempts = cert.attempts && cert.attempts.length > 0;
      const hasNoPassedAttempt = !cert.hasPassedAttempt;
      const allAttemptsExhausted = cert.max_attempts &&
                                    (cert.completedAttempts || 0) >= cert.max_attempts;

      // Show in progress only if they have attempts, haven't passed, and haven't exhausted attempts
      matchesTab = !!hasAttempts && hasNoPassedAttempt && !allAttemptsExhausted;
    } else if (activeTab === 'completed') {
      // Show in COMPLETED tab if:
      // 1. Status is COMPLETED AND at least one attempt has passed=true OR
      // 2. All attempts are exhausted (attempts >= max_attempts) regardless of pass status
      const allAttemptsExhausted = cert.max_attempts &&
                                    (cert.completedAttempts || 0) >= cert.max_attempts;

      matchesTab = (cert.status === 'COMPLETED' && cert.hasPassedAttempt === true) ||
                   !!allAttemptsExhausted;
    } else if (activeTab === 'not_started') {
      // Show only if there are NO attempts at all
      const hasNoAttempts = !cert.attempts || cert.attempts.length === 0;
      matchesTab = hasNoAttempts;
    }

    return matchesSearch && matchesTab;
  });

  // Sort certifications
  const sortedCertifications = [...filteredCertifications].sort((a, b) => {
    switch (sortBy) {
      case 'lastAccessed':
        const aDate = a.last_accessed_at || a.updatedAt;
        const bDate = b.last_accessed_at || b.updatedAt;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      case 'title':
        return a.title.localeCompare(b.title);
      case 'enrolledDate':
        const aCreated = a.createdAt;
        const bCreated = b.createdAt;
        if (!aCreated) return 1;
        if (!bCreated) return -1;
        return new Date(bCreated).getTime() - new Date(aCreated).getTime();
      default:
        return 0;
    }
  });

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-amber-600 to-brand-orange-600 rounded-xl p-4 md:p-6 text-white">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-2 flex items-center gap-2">
              <Trophy className="w-6 h-6 md:w-7 md:h-7" />
              My Certifications
            </h1>
            <p className="text-brand-amber-100 text-sm md:text-base">
              Track your certification progress and manage your exams
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-brand-amber-100 text-xs md:text-sm">Total</p>
            <p className="text-xl md:text-2xl font-bold">
              {dashboardStats?.total_certification || 0}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-brand-amber-100 text-xs md:text-sm">In Progress</p>
            <p className="text-xl md:text-2xl font-bold">
              {dashboardStats?.in_progress_certification || 0}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-brand-amber-100 text-xs md:text-sm">Completed</p>
            <p className="text-xl md:text-2xl font-bold">
              {dashboardStats?.completed_certification || 0}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-brand-amber-100 text-xs md:text-sm">Mandatory</p>
            <p className="text-xl md:text-2xl font-bold">
              {dashboardStats?.mandatory_certification || 0}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max">
            {[
              { key: 'all', label: 'All Certifications', icon: Award, count: certifications.length },
              {
                key: 'in_progress',
                label: 'In Progress',
                icon: Play,
                count: certifications.filter(c => {
                  const hasAttempts = c.attempts && c.attempts.length > 0;
                  const hasNoPassedAttempt = !c.hasPassedAttempt;
                  const allAttemptsExhausted = c.max_attempts &&
                                                (c.completedAttempts || 0) >= c.max_attempts;
                  return hasAttempts && hasNoPassedAttempt && !allAttemptsExhausted;
                }).length
              },
              {
                key: 'completed',
                label: 'Completed',
                icon: CheckCircle,
                count: certifications.filter(c => {
                  const allAttemptsExhausted = c.max_attempts &&
                                                (c.completedAttempts || 0) >= c.max_attempts;
                  return (c.status === 'COMPLETED' && c.hasPassedAttempt === true) ||
                         !!allAttemptsExhausted;
                }).length
              },
              { key: 'not_started', label: 'Not Started', icon: Target, count: certifications.filter(c => !c.attempts || c.attempts.length === 0).length }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex items-center gap-2 px-3 md:px-6 py-3 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-brand-amber-600 text-brand-amber-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
                {tab.count !== null && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Filters and Search */}
        <div className="p-3 md:p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search certifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 md:pl-10 pr-4 py-2 text-sm md:text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-amber-500"
              />
            </div>
            <div className="flex gap-2">
              <div className="flex-1 sm:flex-none sm:w-48">
                <SimpleSelect
                  value={sortBy}
                  onChange={(val) => setSortBy(String(val || 'lastAccessed'))}
                  options={[
                    { value: 'lastAccessed', label: 'Last Accessed' },
                    { value: 'title', label: 'Title' },
                    { value: 'enrolledDate', label: 'Enrolled Date' },
                  ]}
                  placeholder="Sort by"
                  isSearchable={false}
                />
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-brand-amber-100 text-brand-amber-600' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  <Grid3x3 className="w-4 h-4 md:w-5 md:h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-brand-amber-100 text-brand-amber-600' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  <List className="w-4 h-4 md:w-5 md:h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6">
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <CardSkeleton key={i} showImage lines={3} />
              ))}
            </div>
          ) : sortedCertifications.length === 0 ? (
            <div className="text-center py-8 md:py-12">
              <Award className="w-10 h-10 md:w-12 md:h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm md:text-base text-gray-600 px-4">
                {searchTerm ? `No certifications found matching "${searchTerm}"` :
                 activeTab === 'in_progress' ? "You don't have any certifications in progress" :
                 activeTab === 'completed' ? "You haven't completed any certifications yet" :
                 activeTab === 'not_started' ? "You haven't enrolled in any certifications yet" :
                 "No certifications assigned yet"}
              </p>
            </div>
          ) : (
            <>
            {/* Phones get one column, where grid and list look the same: always show the grid there. */}
            <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 ${viewMode === 'list' ? 'sm:hidden' : ''}`}>
              {sortedCertifications.map((certification) => (
                <CompactCertificationCard
                  key={certification.certification_id}
                  certification={certification}
                  onAction={handleCertificationAction}
                  onRatingClick={handleRatingClick}
                  onWriteReview={handleWriteReview}
                  onClaimCertificate={handleClaimCertificate}
                  hasUserReview={reviewedCertificationIds.has(certification.certification_id)}
                  certificateNumber={certCertificateMap?.get(certification.certification_id)}
                  canRead={canRead}
                  canWrite={canWrite}
                />
              ))}
            </div>
            {viewMode === 'list' && (
            <div className="hidden sm:block space-y-3 md:space-y-4">
              {sortedCertifications.map((certification) => (
                <div key={certification.certification_id} className="bg-white rounded-lg border border-gray-200 p-3 md:p-4 hover:shadow-md transition-all">
                  <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                    <div className="w-full sm:w-20 h-32 sm:h-20 rounded-lg overflow-hidden bg-gradient-to-br from-brand-amber-50 to-brand-orange-100 flex-shrink-0">
                      {certification.thumbnail_image_url ? (
                        <img
                          src={certification.thumbnail_image_url}
                          alt={certification.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Award className="w-8 h-8 md:w-10 md:h-10 text-brand-amber-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm md:text-base text-gray-900 truncate">{certification.title}</h3>
                          <p className="text-xs md:text-sm text-gray-600 line-clamp-2">{certification.description}</p>
                        </div>
                        {certification.is_mandatory && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium whitespace-nowrap">
                            Mandatory
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-gray-600 mb-3">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 md:w-4 md:h-4" />
                          {certification.time_limit_minutes || 0}m
                        </span>
                        {/* Show attempts only if max_attempts is defined and allow_retry is not false */}
                        {certification.max_attempts && certification.allow_retry !== false && (
                          <span className="flex items-center gap-1">
                            <Target className="w-3 h-3 md:w-4 md:h-4" />
                            {certification.completedAttempts || 0}/{certification.max_attempts} attempts
                          </span>
                        )}
                        {/* Unlimited attempts message - when max_attempts is null */}
                        {certification.max_attempts === null && (
                          <span className="flex items-center gap-1 text-green-600 font-medium">
                            <CheckCircle className="w-3 h-3 md:w-4 md:h-4" />
                            Unlimited attempts
                          </span>
                        )}
                        {/* No Retry Allowed Message */}
                        {certification.allow_retry === false && certification.max_attempts !== null && (
                          <span className="flex items-center gap-1 text-orange-600 font-medium">
                            <AlertCircle className="w-3 h-3 md:w-4 md:h-4" />
                            No retry allowed
                          </span>
                        )}
                        {certification.deadline_date && (
                          <span className="flex items-center gap-1 text-orange-600">
                            <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                            <span className="hidden sm:inline">Due </span>{new Date(certification.deadline_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                        <div className="flex-1">
                          {certification.status === 'COMPLETED' && certification.completed_at && certification.max_attempts !== null && (
                            <div className="flex items-center gap-2 text-xs md:text-sm text-green-600 font-medium">
                              <CheckCircle className="w-3 h-3 md:w-4 md:h-4" />
                              Completed {new Date(certification.completed_at).toLocaleDateString()}
                            </div>
                          )}
                          {certification.status === 'IN_PROGRESS' && certification.started_at && (
                            <div className="text-xs md:text-sm text-gray-600">
                              Started {new Date(certification.started_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          {(() => {
                            const hasInProgressAttempt = !!certification.inProgressAttemptId;
                            // max_attempts null means unlimited, so never reached
                            const maxAttemptsReached = certification.max_attempts !== null && certification.max_attempts &&
                              (certification.completedAttempts || 0) >= certification.max_attempts;
                            const hasCompletedOrAbandoned = (certification.completedAttempts || 0) > 0 || (certification.abandonedAttempts || 0) > 0;

                            // Show "Continue" if there's an in-progress attempt
                            if (hasInProgressAttempt) {
                              return (
                                <button
                                  onClick={() => canWrite && handleCertificationAction(certification.certification_id, 'continue')}
                                  disabled={!canWrite}
                                  title={!canWrite ? 'You do not have permission to access certifications' : undefined}
                                  className={`flex-1 sm:flex-none px-3 md:px-4 py-2 rounded-lg font-medium text-xs md:text-sm ${
                                    canWrite
                                      ? 'bg-brand-blue-600 text-white hover:bg-brand-blue-700'
                                      : 'bg-brand-blue-600 text-white opacity-50 cursor-not-allowed'
                                  }`}
                                >
                                  Continue
                                </button>
                              );
                            }

                            // Show "Completed" if max attempts reached (only when max_attempts is defined)
                            if (maxAttemptsReached) {
                              return (
                                <button
                                  disabled
                                  className="flex-1 sm:flex-none px-3 md:px-4 py-2 bg-gray-400 text-white rounded-lg cursor-not-allowed font-medium text-xs md:text-sm flex items-center gap-1"
                                >
                                  <CheckCircle className="w-3 h-3 md:w-4 md:h-4" />
                                  Completed
                                </button>
                              );
                            }

                            // Show "Retry" if completed/abandoned but not reached max attempts and allow_retry is true
                            if (hasCompletedOrAbandoned && !maxAttemptsReached) {
                              const canRetry = certification.allow_retry !== false && canWrite;
                              return (
                                <button
                                  onClick={() => canRetry && handleCertificationAction(certification.certification_id, 'start')}
                                  disabled={!canRetry}
                                  title={!canWrite ? 'You do not have permission to access certifications' : undefined}
                                  className={`flex-1 sm:flex-none px-3 md:px-4 py-2 rounded-lg font-medium text-xs md:text-sm ${
                                    canRetry
                                      ? 'bg-orange-600 text-white hover:bg-orange-700'
                                      : 'bg-gray-400 text-white cursor-not-allowed opacity-50'
                                  }`}
                                >
                                  Retry
                                </button>
                              );
                            }

                            // Default: Show "Start Exam"
                            return (
                              <button
                                onClick={() => canWrite && handleCertificationAction(certification.certification_id, 'start')}
                                disabled={!canWrite}
                                title={!canWrite ? 'You do not have permission to access certifications' : undefined}
                                className={`flex-1 sm:flex-none px-3 md:px-4 py-2 rounded-lg font-medium text-xs md:text-sm ${
                                  canWrite
                                    ? 'bg-brand-amber-600 text-white hover:bg-brand-amber-700'
                                    : 'bg-brand-amber-600 text-white opacity-50 cursor-not-allowed'
                                }`}
                              >
                                Start Exam
                              </button>
                            );
                          })()}
                          {/* Claim Certificate Button */}
                          {certCertificateMap?.get(certification.certification_id) && (
                            <button
                              onClick={() => handleClaimCertificate(certCertificateMap.get(certification.certification_id)!)}
                              className="flex-1 sm:flex-none px-3 md:px-4 py-2 bg-brand-amber-500 text-white hover:bg-brand-amber-600 rounded-lg font-medium flex items-center justify-center gap-2 text-xs md:text-sm transition-colors"
                            >
                              <Award className="w-3 h-3 md:w-4 md:h-4" />
                              <span className="hidden sm:inline">Claim Certificate</span>
                              <span className="sm:hidden">Claim</span>
                            </button>
                          )}
                          {/* View Result Button - Shown if show_feedback is true AND at least one attempt exists (completed, submitted, or abandoned) */}
                          {(() => {
                            const canViewResults = certification.show_feedback !== false;
                            const hasViewableAttempt =
                              certification.hasCompletedAttempt === true ||
                              (certification.abandonedAttempts ?? 0) > 0;
                            if (!canViewResults || !hasViewableAttempt) return null;
                            return (
                              <button
                                onClick={() => handleCertificationAction(certification.certification_id, 'viewResult')}
                                className="flex-1 sm:flex-none px-3 md:px-4 py-2 border rounded-lg font-medium flex items-center justify-center gap-2 text-xs md:text-sm transition-colors border-brand-amber-600 bg-white text-brand-amber-600 hover:bg-brand-amber-50 cursor-pointer"
                              >
                                <FileText className="w-3 h-3 md:w-4 md:h-4" />
                                <span className="hidden sm:inline">View Results</span>
                                <span className="sm:hidden">Results</span>
                              </button>
                            );
                          })()}
                          {/* Write Review Button - Hidden if user already reviewed */}
                          {!reviewedCertificationIds.has(certification.certification_id) && (
                            <button
                              onClick={() => canWrite && handleWriteReview(certification.certification_id)}
                              disabled={!canWrite}
                              title={!canWrite ? 'You do not have permission to write reviews' : undefined}
                              className={`flex-1 sm:flex-none px-3 md:px-4 py-2 border rounded-lg font-medium flex items-center justify-center gap-2 text-xs md:text-sm transition-colors ${
                                canWrite
                                  ? 'border-brand-purple-600 text-brand-purple-600 hover:bg-brand-purple-50'
                                  : 'border-gray-200 text-gray-400 opacity-50 cursor-not-allowed'
                              }`}
                            >
                              <PenLine className="w-3 h-3 md:w-4 md:h-4" />
                              <span className="hidden sm:inline">Write Review</span>
                              <span className="sm:hidden">Review</span>
                            </button>
                          )}
                          <Link
                            to={canRead ? `/learn/certification/${certification.certification_id}` : '#'}
                            onClick={(e) => !canRead && e.preventDefault()}
                            title={!canRead ? 'You do not have permission to access certifications' : undefined}
                            className={`flex-1 sm:flex-none px-3 md:px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 text-xs md:text-sm ${
                              canRead
                                ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                : 'bg-white border border-gray-200 text-gray-400 opacity-50 cursor-not-allowed'
                            }`}
                          >
                            <Eye className="w-3 h-3 md:w-4 md:h-4" />
                            <span>Details</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
